import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface SelectOptionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** Unique value for this option */
  value: string
  /** Whether this option is currently selected */
  selected?: boolean
  /** Whether this option is disabled */
  disabled?: boolean
  /** Whether this option is currently highlighted/focused */
  highlighted?: boolean
  /** Called when this option is clicked */
  onSelect?: (value: string) => void
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled SelectOption component.
 *
 * Represents a single option within a Select dropdown.
 * All visual state determined by props. Spreads ...props for HOC injection.
 */
const SelectOption = React.memo(
  ({
    value,
    selected = false,
    disabled = false,
    highlighted = false,
    onSelect,
    children,
    style,
    onClick,
    onKeyDown,
    className,
    ...props
  }: SelectOptionProps) => {
    const [css, theme] = useStyletron()

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return
      onSelect?.(value)
      onClick?.(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect?.(value)
      }
      onKeyDown?.(e)
    }

    const optionClassName = css(
      getOptionStyles(theme, { selected, disabled, highlighted })
    )

    return (
      <div
        role="option"
        aria-selected={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-value={value}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${optionClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface StyleParams {
  selected: boolean
  disabled: boolean
  highlighted: boolean
}

const getOptionStyles = (
  theme: any,
  { selected, disabled, highlighted }: StyleParams
) => {
  const getBackgroundColor = () => {
    if (disabled) return 'transparent'
    if (selected) return theme.colors.primary50
    if (highlighted) return theme.colors.grey10
    return 'transparent'
  }

  return {
    padding: '10px 12px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    lineHeight: '20px',
    color: disabled ? theme.colors.grey50 : theme.colors.grey70,
    backgroundColor: getBackgroundColor(),
    fontWeight: selected && !disabled ? 500 : 'normal',
    transition: 'background-color 150ms ease',
  }
}

export default SelectOption
